import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, MapPin, Pin, PinOff, Star, X } from 'lucide-react';
import type { IFrequentDestination, ILocation } from '@carrierbridge/shared';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn, formatNumber, normalizeText } from '@/lib/utils';

/** Lieux listés au plus dans la recherche libre — au-delà, on affine. */
const SEARCH_LIMIT = 30;

/**
 * Choix d'un lieu : recherche libre, mais les bons candidats d'abord.
 *
 * Une liste déroulante native sur un carnet de plusieurs milliers d'adresses
 * demande de connaître le nom exact avant de commencer à chercher, et range
 * par ordre alphabétique — un ordre qui ne sait rien du métier. Or une
 * carrière part toujours du même site et livre presque toujours les mêmes
 * chantiers.
 *
 * D'où trois strates dans le même contrôle, de la plus informée à la plus
 * générale : le défaut de l'utilisateur, les destinations qu'il sert le plus
 * depuis l'origine choisie, puis tout le carnet. Rien n'est retiré — la
 * recherche libre reste le fond de la liste.
 */
export function LocationPicker({
  label,
  locations,
  value,
  onChange,
  frequent = [],
  defaultLocationId = null,
  onSetDefault,
  placeholder,
}: {
  label: string;
  locations: ILocation[];
  value: string | null;
  onChange: (location: ILocation | null) => void;
  /** Destinations les plus servies depuis l'origine — vide côté origine. */
  frequent?: IFrequentDestination[];
  /** Défaut enregistré par l'utilisateur, épinglé en tête. */
  defaultLocationId?: string | null;
  /** Absent = ce côté n'est pas paramétrable comme défaut. */
  onSetDefault?: (locationId: string | null) => void;
  placeholder: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const boxRef = useRef<HTMLDivElement>(null);

  const byId = useMemo(() => new Map(locations.map((l) => [l.id, l])), [locations]);
  const selected = value ? (byId.get(value) ?? null) : null;

  // Fermeture au clic extérieur : sans elle, la liste reste ouverte par-dessus
  // le reste du formulaire et masque les champs qu'on va remplir ensuite.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const needle = normalizeText(query.trim());
  const matches = useMemo(() => {
    if (!needle) return locations.slice(0, SEARCH_LIMIT);
    return locations
      .filter((l) =>
        normalizeText(
          `${l.nom} ${l.lieu?.ville ?? ''} ${l.lieu?.codePostal ?? ''}`,
        ).includes(needle),
      )
      .slice(0, SEARCH_LIMIT);
  }, [locations, needle]);

  // Les strates informées ne s'affichent que sur une liste non filtrée : dès
  // qu'on tape, c'est qu'on cherche autre chose que ce qu'on propose.
  const pinned = !needle && defaultLocationId ? byId.get(defaultLocationId) : undefined;
  const frequentRows = needle
    ? []
    : frequent.filter((f) => f.locationId !== pinned?.id).slice(0, 8);
  const frequentIds = new Set([...frequentRows.map((f) => f.locationId), pinned?.id]);
  const rest = matches.filter((l) => !frequentIds.has(l.id));

  function pick(location: ILocation | null) {
    onChange(location);
    setOpen(false);
    setQuery('');
  }

  const isDefault = !!selected && selected.id === defaultLocationId;

  return (
    <div className="relative" ref={boxRef}>
      <div className="flex items-center gap-1">
        <p className="text-[13px] font-medium text-text">{label}</p>
        {onSetDefault && selected && (
          // Épingler se fait là où l'on choisit : un réglage rangé dans une
          // page de préférences ne serait posé par personne.
          <Button
            type="button"
            variant="ghost"
            size="xs"
            onClick={() => onSetDefault(isDefault ? null : selected.id)}
            title={isDefault ? t('quote.defaults.unset') : t('quote.defaults.set')}
            className={cn(
              'ml-auto h-6 px-1.5 text-2xs',
              isDefault ? 'text-accent hover:text-danger' : 'text-subtle hover:text-accent',
            )}
          >
            {isDefault ? <PinOff className="h-3 w-3" /> : <Pin className="h-3 w-3" />}
            {isDefault ? t('quote.defaults.unsetShort') : t('quote.defaults.setShort')}
          </Button>
        )}
      </div>

      {/* Champ de sélection : le déclencheur et l'effacement sont frères (pas de bouton dans un bouton). */}
      <div className="mt-1.5 flex items-center rounded border border-border bg-surface px-3.5 py-2.5 transition-colors focus-within:border-accent focus-within:ring-1 focus-within:ring-accent hover:border-accent/40">
        {/* eslint-disable-next-line no-restricted-syntax -- déclencheur de liste déroulante : se présente comme un champ, pas comme un bouton d'action */}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="flex min-w-0 flex-1 items-center gap-2 text-left text-sm text-text focus:outline-none"
        >
          <MapPin className="h-3.5 w-3.5 shrink-0 text-subtle" aria-hidden="true" />
          <span className={cn('min-w-0 flex-1 truncate', !selected && 'text-subtle')}>
            {selected ? selected.nom : placeholder}
          </span>
        </button>
        {selected && (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={t('common.clear')}
            onClick={() => pick(null)}
            className="-my-1 -mr-2 shrink-0 text-subtle hover:text-danger"
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>

      {selected?.lieu && (
        <p className="mt-1 text-2xs text-subtle">
          {[selected.lieu.codePostal, selected.lieu.ville].filter(Boolean).join(' ') || (
            <span className="text-warning">{t('quote.locationWithoutAddress')}</span>
          )}
        </p>
      )}

      {open && (
        <div className="absolute z-30 mt-1 w-full rounded border border-border bg-surface shadow-modal">
          <div className="border-b border-border p-2">
            <Input
              // eslint-disable-next-line jsx-a11y/no-autofocus -- la liste vient de s'ouvrir sur demande : le focus va au champ de recherche
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label={t('quote.defaults.searchPlaceholder')}
              placeholder={t('quote.defaults.searchPlaceholder')}
              className="py-1.5 text-xs"
            />
          </div>

          <ul className="max-h-64 overflow-y-auto py-1 text-xs">
            {pinned && (
              <Row
                location={pinned}
                selected={pinned.id === value}
                onPick={pick}
                badge={
                  <span className="inline-flex items-center gap-1 text-2xs text-accent">
                    <Star className="h-2.5 w-2.5" />
                    {t('quote.defaults.yourDefault')}
                  </span>
                }
              />
            )}

            {frequentRows.length > 0 && (
              <li className="px-2 py-1 text-2xs font-semibold uppercase tracking-wide text-subtle">
                {t('quote.defaults.frequentGroup')}
              </li>
            )}
            {frequentRows.map((f) => {
              const location = byId.get(f.locationId);
              if (!location) return null;
              return (
                <Row
                  key={f.locationId}
                  location={location}
                  selected={f.locationId === value}
                  onPick={pick}
                  badge={
                    <span className="text-2xs tabular-nums text-subtle">
                      {t('quote.defaults.shipments', {
                        count: f.shipments,
                        formatted: formatNumber(f.shipments),
                      })}
                    </span>
                  }
                />
              );
            })}

            {rest.length > 0 && (frequentRows.length > 0 || pinned) && (
              <li className="px-2 py-1 text-2xs font-semibold uppercase tracking-wide text-subtle">
                {t('quote.defaults.allGroup')}
              </li>
            )}
            {rest.map((l) => (
              <Row key={l.id} location={l} selected={l.id === value} onPick={pick} />
            ))}

            {matches.length === 0 && (
              <li className="px-2 py-2 text-2xs text-subtle">{t('common.noResults')}</li>
            )}
            {/* Un plafond qu'on ne dit pas se lit comme un carnet plus petit. */}
            {matches.length === SEARCH_LIMIT && (
              <li className="border-t border-border px-2 py-1 text-xs text-subtle">
                {t('quote.defaults.refine', { count: SEARCH_LIMIT })}
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

function Row({
  location,
  selected,
  onPick,
  badge,
}: {
  location: ILocation;
  selected: boolean;
  onPick: (l: ILocation) => void;
  badge?: React.ReactNode;
}) {
  const detail = [location.lieu?.codePostal, location.lieu?.ville].filter(Boolean).join(' ');
  return (
    <li>
      {/* eslint-disable-next-line no-restricted-syntax -- ligne d'une liste de choix (item de menu), pas un bouton d'action */}
      <button
        type="button"
        onClick={() => onPick(location)}
        className={cn(
          'flex w-full items-center gap-2 px-2 py-1.5 text-left hover:bg-surface-subtle',
          selected && 'bg-accent-soft',
        )}
      >
        {selected ? (
          <Check className="h-3 w-3 shrink-0 text-accent" />
        ) : (
          <span className="w-3 shrink-0" />
        )}
        <span className="min-w-0 flex-1 truncate text-text">
          {location.nom}
          {detail && <span className="ml-1.5 text-subtle">{detail}</span>}
        </span>
        {badge}
      </button>
    </li>
  );
}
